import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewContainerRef,
} from '@angular/core';
import { ModalDialogueService } from '../modal-dialogue/modal-dialogue.service';
import { DatetimePickerComponent } from '../datetime-picker/datetime-picker.component';
import {
  getMultiSelectSearchValue,
  sortArrayOfObject,
} from '../../../../global.variable';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'gtapp-gt-custom-table',
  standalone: false,
  templateUrl: './gt-custom-table.component.html',
  styleUrl: './gt-custom-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GtCustomTableComponent implements OnInit, OnDestroy {
  trackByColumn = (_: number, col: any) => col?.name;
  trackByRow = (_: number, row: any) => row?.id ?? _;
  trackByAction = (_: number, action: any) => action?.key ?? _;
  trackByStatus = (_: number, status: any) => status?.id ?? status?.name;

  @Output() actionClick = new EventEmitter();
  @Output() rowClick = new EventEmitter();
  @Output() searchColumn = new EventEmitter();
  @Output() sortColumn = new EventEmitter();
  @Output() toggleColumn = new EventEmitter();
  @Output() paginationChange = new EventEmitter();
  @Output() checkBoxClicked = new EventEmitter();

  //pagination params

  totalRows: number = 0;
  previousRows: number = 0;
  rows: number = 15;
  pageNum: number = 1;
  totalPages: number = 0;

  showTopPaginator = true;

  selectedIndex: any;

  selectedDropDownItem: any = {};

  autoCompleteSearchResultList: any = [];

  @Input() tableStyle: any;

  tableData = signal<any[]>([]);
  tableColumns = signal<any[]>([]);
  apiFiltering = signal(false);
  tableCustomParams = signal<any>({});
  desktopTableDataTemp = signal<any[]>([]);

  @Input() set deskTopTableData(value: any[]) {
    if (value) {
      this.tableData.set(value);
      this.desktopTableDataTemp.set(value);
    }
    setTimeout(() => this.checkIfBottomIsVisible(), 0);
  }

  @Input() set desktopTableDetails(value: any[]) {
    if (value) {
      this.tableColumns.set(value);
    }
  }
  @Input() set otherParams(value: any) {
    this.apiFiltering.set(value?.apiFiltering);

    this.tableCustomParams.set(value);

    if (this.apiFiltering()) {
      this.totalRows = value?.paginationData?.totalRows;
      this.previousRows = value?.paginationData?.previousRows;
      this.rows = value?.paginationData?.rows;
      this.pageNum = value?.paginationData?.pageNum;
      this.totalPages = value?.paginationData?.totalPages;
    } else {
      this.apiFiltering.set(false);
      const previous = value?.paginationData?.totalRows ? this.previousRows : 0;

      this.rows = value?.paginationData?.rows || this.rows;

      this.totalRows =
        value?.paginationData?.totalRows || this.desktopTableDataTemp()?.length;

      this.totalPages = Math.ceil(this.totalRows / this.rows);
      this.tableData.set(
        this.desktopTableDataTemp()?.slice(
          previous,
          previous ? previous + this.rows : this.rows,
        ),
      );
    }
  }

  constructor(
    private dialogService: ModalDialogueService,
    private cdr: ChangeDetectorRef,
    private viewContainerRef: ViewContainerRef,
  ) {}

  ngOnInit(): void {}
  ngOnDestroy(): void {}

  checkIfBottomIsVisible() {
    const bottomPaginator: any = document.getElementById('bottom-paginator');
    if (!bottomPaginator) return;

    const rect = bottomPaginator?.getBoundingClientRect();

    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;

    // If the top edge of the bottom pagination is below the screen height, it's hidden
    this.showTopPaginator = rect.top >= viewportHeight;

    this.cdr.detectChanges();
  }

  toggleField(column: any) {
    this.toggleColumn.emit(column);
  }
  sortField(column: any) {
    // Simplify the sortOrder assignment
    column['sortOrder'] = column?.sortOrder === 'asc' ? 'desc' : 'asc';

    // Reset sortOrder for other columns
    this.tableColumns().forEach((item: any) => {
      if (item?.name !== column?.name) {
        item.sortOrder = null;
      }
    });

    // Check if API filtering is enabled
    if (this.apiFiltering()) {
      let sortKeyValue: any = column?.sortKey || column?.name;
      let sortKey: any =
        column?.sortOrder == 'desc' ? `-${sortKeyValue}` : sortKeyValue;
      let body = this.getSearchParams();

      body.sortColumn = column;
      if (sortKey) body.sortList = [sortKey];
      this.sortColumn.emit(body);
    } else {
      if (column?.sortOrder) {
        let sortKey = column?.name || column?.sortKey;
        if (column?.datetimeObj) {
          this.desktopTableDataTemp().sort((a: any, b: any) => {
            let dateA = new Date(a[sortKey]).getTime();
            let dateB = new Date(b[sortKey]).getTime();
            return column?.sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
          });
        } else {
          this.desktopTableDataTemp.set(
            sortArrayOfObject(
              this.desktopTableDataTemp(),
              sortKey,
              column?.sortOrder,
            ),
          );
        }
      }
      this.filterTableData();
    }
  }
  onUpdateParent(event: any) {
    if (event?.action === 'removeCol') {
      this.removeFilter(event?.col);
    }
    if (event?.action === 'updateTableCols') {
      this.tableColumns.set([...event?.cols]);

      this.applyFilters();
    }
  }

  async openMultipleSelect(column: any, template: any) {
    const dialogRef = this.dialogService.open(
      template,
      { data: column },
      this.viewContainerRef,
    );
    const res = await firstValueFrom(dialogRef.afterClosed());
    if (res) {
      const updatedColumns = this.tableColumns()?.map((col) =>
        col === column
          ? {
              ...col,
              searchValue: getMultiSelectSearchValue(column),
              searchString: null,
              openSearchBox: false,
              selectedItems: column?.selectedItems,
            }
          : col,
      );

      this.tableColumns.set(updatedColumns);
      this.applyFilters();
    }
  }

  openRangePicker(column: any) {
    const dateRange = {
      start: column?.searchValue?.start || column?.dateRange?.start,
      end: column?.searchValue?.end || column?.dateRange?.end,
    };

    const dialogeRef = this.dialogService.open(DatetimePickerComponent, {
      data: { dateRange: dateRange },
    });
    dialogeRef.afterClosed().subscribe((value: any) => {
      if (value?.start && value?.end) {
        column.searchValue = value;
        const updatedColumns = this.tableColumns()?.map((col) =>
          col === column
            ? {
                ...col,
                searchValue: col.searchValue,
                searchString: null,
                openSearchBox: false,
              }
            : col,
        );

        this.tableColumns.set(updatedColumns);
        this.applyFilters();
      }
    });
  }
  onActionClick(event: any, type: any, index: number) {
    const disableClicking = this.tableCustomParams()?.disableClicking || false;
    if (!disableClicking) this.selectedIndex = index;
    this.actionClick.emit({ event, type });
  }
  onTableRowClick(rowData: any, index: any) {
    const disableClicking = this.tableCustomParams()?.disableClicking || false;
    if (!disableClicking) this.selectedIndex = index;
    this.rowClick.emit(rowData);
  }
  onCheckBoxChange(event: any, data: any) {
    const isChecked = event.target.checked;

    data.checboxTicked = isChecked;
    // this.allItemsSelected = this.tableData?.every(
    //   (item: any) => item?.checboxTicked
    // );
    this.checkBoxClicked.emit({ ...data, checked: isChecked });
  }

  onChangePagination(event: any) {
    this.previousRows = event.previous;
    this.pageNum = event.pageNum;
    this.rows = event.pageSize;
    if (this.apiFiltering()) {
      this.paginationChange.emit({
        paginationData: {
          totalRows: this.totalRows,
          previousRows: this.previousRows,
          rows: this.rows,
          pageNum: this.pageNum,
          totalPages: this.totalPages,
        },
      });
    } else {
      this.tableData.set(
        this.desktopTableDataTemp()?.slice(
          this.previousRows,
          this.rows * this.pageNum,
        ),
      );
    }
  }
  getSearchParams() {
    const searchParams: any = {};
    let body: any = {};

    this.tableColumns()?.forEach((col: any) => {
      if (!col?.searchValue && col?.searchValue !== 0) return;

      if (col?.datetimeObj) {
        const field = col?.dateRangeKey || col?.name || 'updated_at';
        searchParams[`${field}__range`] = [
          col.searchValue.start,
          col.searchValue.end,
        ];
      } else if (col?.dropdownKey) {
        if (col?.multipleSelect) {
          searchParams[`${col.dropdownKey}__in`] = col?.selectedItems;
        } else {
          searchParams[col.dropdownKey] = col.searchValue;
        }
      } else if (col?.autoCompleteSelectedId) {
        switch (col?.autoComplete) {
          case 'user':
            body['sub_user_id'] = col?.autoCompleteSelectedId;
            break;
          case 'site':
            body['site_id'] = col?.autoCompleteSelectedId;
            break;
        }
        const key = col?.searchKey;
        if (key) searchParams[`${key}__icontains`] = col.searchValue;
      } else {
        const key = col?.searchKey || col?.name;
        searchParams[`${key}__icontains`] = col.searchValue;
      }
    });

    return { ...body, searchParams };
  }
  applyFilters() {
    if (this.apiFiltering()) {
      this.searchColumn.emit(this.getSearchParams());
    } else {
      this.filterTableData();
      this.searchColumn.emit({
        updateRouteOnly: true,
        ...this.getSearchParams(),
      });
    }
  }
  removeFilter(column: any) {
    const updatedColumns = this.tableColumns()?.map((col) =>
      col === column
        ? {
            ...col,
            searchValue: null,
            searchString: null,
            openSearchBox: false,
          }
        : col,
    );

    this.tableColumns.set(updatedColumns);

    this.applyFilters();
  }
  filterTableData() {
    const filteredData = this.tableColumns()?.reduce((acc: any, col: any) => {
      if (!col?.searchValue && col?.searchValue !== 0) return acc;
      return acc?.filter((item: any) =>
        col?.datetimeObj
          ? this.filterByDate(item, col)
          : this.filterByValue(item, col),
      );
    }, this.desktopTableDataTemp() || []);
    this.tableData.set(filteredData);
    if (this.tableCustomParams()?.paginationData) {
      this.totalRows = filteredData?.length;
      this.previousRows = 0;
      this.pageNum = 1;
      this.totalPages = Math.ceil(this.totalRows / this.rows);
      this.tableData.set(filteredData?.slice(0, this.rows));
    }
  }
  filterByDate(item: any, element: any) {
    let elementField = element?.field ? element?.field : 'updated_at';
    let itemTime = new Date(item?.[elementField]).getTime();
    return (
      itemTime >= element?.searchValue?.start.getTime() &&
      itemTime <= element?.searchValue?.end.getTime()
    );
  }

  filterByValue(item: any, element: any) {
    let colValueList: any = [];

    const columnValue = element?.nestedValue
      ? element?.nestedValue(item) || item?.[element?.name]
      : item?.[element?.name];
    if (columnValue) colValueList.push(columnValue?.toLowerCase());
    if (element?.prefixNestedValue && element?.prefixNestedValue(item))
      colValueList.push(element?.prefixNestedValue(item)?.toLowerCase());
    if (element?.suffixNestedValue && element?.suffixNestedValue(item))
      colValueList.push(element?.suffixNestedValue(item)?.toLowerCase());

    const match = colValueList?.some((val: any) =>
      val.toLowerCase().includes(element?.searchValue?.toLowerCase()),
    );
    return match;
  }
}
